import type React from "react";

import { Box } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

import { useAppConfigContext } from "contexts/AppConfigContext/useAppConfigContext";

interface ListItemProps {
  dataTestId?: string;
  icon?: React.ReactNode;
  iconActive?: React.ReactNode;
  label: string;
  notificationCount?: number;
  onClick?: () => void;
  showBackgroundOnActive?: boolean;
  to: string;
}

export const ListItem: React.FC<ListItemProps> = ({
  dataTestId,
  icon,
  iconActive,
  label,
  notificationCount,
  onClick,
  showBackgroundOnActive,
  to,
}) => {
  const location = useLocation();
  const { navBarCollapsed } = useAppConfigContext();
  const externalLink = to.startsWith("http://") || to.startsWith("https://");

  const currentRoot = `/${location.pathname.split("/")[1]}`;
  const linkRoot = `/${to.split("/")[1]}`;
  const match = currentRoot === linkRoot;

  const iconToShow = match && iconActive ? iconActive : icon;

  const getContent = () => (
    <>
      {iconToShow && (
        <Box sx={{ fontSize: 20, width: 20, display: "flex", justifyContent: "center" }}>{iconToShow}</Box>
      )}
      {!navBarCollapsed && (
        <Box sx={{ flex: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</Box>
      )}
      {!!notificationCount && <Badge $navBarCollapsed={!!navBarCollapsed}>{notificationCount}</Badge>}
    </>
  );

  if (externalLink) {
    return (
      <StyledLi data-testid={dataTestId}>
        <ExternalLink
          href={to}
          $active={false}
          $navBarCollapsed={navBarCollapsed}
          onClick={onClick}
          target="_blank"
          rel="noopener noreferrer"
        >
          {getContent()}
        </ExternalLink>
      </StyledLi>
    );
  }

  return (
    <StyledLi data-testid={dataTestId}>
      <InternalLink
        to={to}
        $active={!!match}
        data-active={!!match}
        $navBarCollapsed={navBarCollapsed}
        $showBackgroundOnActive={showBackgroundOnActive}
        onClick={onClick}
      >
        {getContent()}
      </InternalLink>
    </StyledLi>
  );
};

const StyledLi = styled.li`
  display: flex;
  box-sizing: border-box;
  user-select: none;
  position: relative;
`;

const linkStyles = css<{ $active?: boolean; $navBarCollapsed?: boolean; $showBackgroundOnActive?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: ${props => (props.$navBarCollapsed ? "center" : "flex-start")};
  align-items: center;
  gap: 8px;
  padding: 12px;
  font-size: 14px;
  width: 100%;
  border-radius: 8px;
  background-color: ${props =>
    props.$active && props.$showBackgroundOnActive ? props.theme.colors.redesign.b10 : "transparent"};
  color: ${props => (props.$active ? props.theme.colors.redesign.b100 : props.theme.colors.primary.base)};
  text-decoration: none;
  transition: color 0.25s ease-in-out;

  &:hover {
    color: ${props => props.theme.colors.redesign.b100};
  }
`;

const InternalLink = styled(NavLink)<{
  $active: boolean;
  $navBarCollapsed?: boolean;
  $showBackgroundOnActive?: boolean;
}>`
  ${linkStyles}
`;

const ExternalLink = styled.a<{ $active: boolean; $navBarCollapsed?: boolean; $showBackgroundOnActive?: boolean }>`
  ${linkStyles}
`;

const Badge = styled.div<{ $navBarCollapsed: boolean }>`
  width: 24px;
  height: 100%;
  min-width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 11px;
  border-radius: 4px;
  background-color: ${props => props.theme.colors.redesign.r100};
  color: ${props => props.theme.colors.white};

  ${props =>
    props.$navBarCollapsed &&
    `
    position: absolute;
    right: 6px;
    top: 6px;
    color: transparent;
    min-width: 12px;
    width: 12px;
    height: 12px;
  `}
`;
